import { Box, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useShowInHomeLockToggle } from "@src/appV2/ExperimentalHomeHealth/lib/useShowInHomeLockToggle";
import { useWorker } from "@src/appV2/Worker/api/useWorker";

import { Avatar } from "../components/Avatar";
import { IconButton } from "../components/IconButton";
import { Text } from "../components/Text";
import { ChatHeaderActions } from "./components/ChatHeaderActions";
import { InHomeLockToggle } from "./InHomeLock/components/InHomeLockToggle";

export interface ChatHeaderProps {
  workerId: string;
  workerName: string;
  onClose: () => void;
  onViewProfile?: (workerId: string) => void;
  showViewProfile?: boolean;
}

/**
 * Header component for the chat panel.
 * Displays worker avatar, name, qualification, and action buttons.
 */
export function ChatHeader(props: ChatHeaderProps) {
  const { workerId, workerName, onClose, onViewProfile, showViewProfile } = props;

  // Fetch worker details for avatar and qualification
  const { data: worker } = useWorker(workerId);

  const session = useSession();
  const workplaceId = session.userId;
  const showLockToggle = useShowInHomeLockToggle();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Left: Worker info */}
      <Stack direction="row" alignItems="center" gap={2}>
        <Avatar size="medium" src={worker?.profileImageUrl ?? undefined} alt={workerName} />
        <Stack direction="column" gap={1}>
          <Text
            semibold
            variant="h6"
            sx={(theme) => ({
              color: theme.palette.base?.base,
            })}
          >
            {workerName}
          </Text>
          {worker?.qualification && (
            <Text
              variant="caption"
              sx={(theme) => ({
                color: theme.palette.base?.inactive,
              })}
            >
              {worker.qualification}
            </Text>
          )}
        </Stack>
      </Stack>

      {/* Right: Actions */}
      <Stack direction="row" alignItems="center" gap={2}>
        {showLockToggle && <InHomeLockToggle workerId={workerId} workplaceId={workplaceId} />}
        <ChatHeaderActions
          workerId={workerId}
          showViewProfile={showViewProfile}
          onViewProfile={onViewProfile}
        />
        <IconButton
          aria-label="Close chat"
          iconType="close"
          size="small"
          variant="outlined"
          onClick={onClose}
        />
      </Stack>
    </Box>
  );
}
